import { useCallback } from 'react';

// ToDo: WW-7459 Move to shared shadow folder
import { InfiniteRow } from '../../../../../../../erp/components/tab-panels/positions/components/edit-table/InfiniteList';
import { EditTableEntry, EditTablePropsAll } from '../types';

interface UseEditableRows<T extends EditTableEntry>
  extends Pick<
    EditTablePropsAll<T>,
    'decorators' | 'editableRows' | 'onMoveComponent'
  > {
  data: React.RefObject<T[]>;
  allCells: string[];
  onBeforeDrag?: () => void;
}

export function useEditableRow<T extends EditTableEntry>(
  options: UseEditableRows<T>
) {
  const {
    decorators,
    editableRows,
    data,
    onMoveComponent: onMove,
    allCells,
  } = options;

  const calculateRowMove = useCallback(
    (row: InfiniteRow<T>) => {
      const isMoveAllowed = (from: T, to: T) => {
        if (to && from) {
          return true;
        }
        return false;
      };

      const canDrop = (input: { id: string; index: number }): number => {
        if (!input) return 0;
        const from = data.current.find((x) => x.localId === input.id);
        return isMoveAllowed(from, row.original) ? input.index - row.index : 0;
      };

      const handleMoveRow = (id: string, index: number) => {
        const from = data.current.find((x) => x.localId === id);
        const toPosition = data.current[index];
        if (isMoveAllowed(from, toPosition)) {
          onMove(id, index);
        }
      };

      return {
        handleMoveRow,
        canDrop,
        isMoveAllowed,
        getHovered: () => row.original,
        getCurrent: (id: string) => data.current.find((x) => x.localId === id),
        onBeforeDrag: options.onBeforeDrag,
      };
    },
    [data, onMove, options.onBeforeDrag]
  );

  const calculateEditableCols = useCallback(
    (position: T) => {
      const relation = decorators?.getRelation
        ? decorators.getRelation(position)
        : 'normal';
      const isNormal = relation === 'normal';

      const editableCols = (editableRows[position.positionKind] ||
        allCells.filter((x) =>
          decorators?.isEditableCell
            ? decorators.isEditableCell(x, position)
            : true
        )) as string[];

      return {
        isNormal,
        editableCols,
      };
    },
    [allCells, decorators, editableRows]
  );

  const calculateRowProperties = useCallback(
    (row: InfiniteRow<T>) => {
      const cols = calculateEditableCols(row.original);

      return {
        ...cols,
        ...calculateRowMove(row),
      };
    },
    [calculateEditableCols, calculateRowMove]
  );
  return {
    calculateRowProperties,
    calculateEditableCols,
  };
}
